<template>
	<div class="wrap wrap_overview">
		<section class="section_overview">
			<div
				class="overview_left"
				:class="!props.auth && 'locked not-draggable cursor-pointer'"
				@click="!props.auth && modalStore.openModal('GuideModal')"
			>
				<div>
					<h4 class="report_title_sub">투자점수</h4>
					<div class="title_score">
						<strong>{{ props.data.invest_score.score }}</strong
						><span>점</span>
					</div>
					<p v-if="props.data.invest_score.price_dollar">기준가 ${{ props.data.invest_score.price_dollar }}</p>
					<p v-if="props.data.invest_score.price_won">
						({{ priceFormat(Number(props.data.invest_score.price_won)) }}원)
					</p>
				</div>
				<img v-if="!props.auth" src="@/assets/img/common/lock_overview.svg" loading="lazy" alt="자물쇠 이미지" />
			</div>
			<div class="overview_right">
				<div class="tooltip_container">
					<button
						class="btn_tooltip"
						@mousemove="
							deviceStore.getDevice === 'MO' ? (isTooltipOpened = !isTooltipOpened) : (isTooltipOpened = true)
						"
						@mouseout="isTooltipOpened = false"
					>
						<img src="@/assets/img/common/question.svg" alt="도움말" loading="lazy" />
					</button>
					<div v-if="isTooltipOpened" class="box_tooltip">
						<p class="tooltip_title">
							{{ COMPANY_ANALYSIS_TOOLTIP }}
						</p>
					</div>
				</div>
				<div class="right_row">
					<div>
						<h4 class="report_title_sub">PER</h4>
						<p>{{ props.data.top_box.PER }}</p>
					</div>
					<div>
						<h4 class="report_title_sub">PBR</h4>
						<p>{{ props.data.top_box.PBR }}</p>
					</div>
				</div>
				<div class="right_row">
					<div>
						<h4 class="report_title_sub">ROE</h4>
						<p>{{ props.data.top_box.ROE }}</p>
					</div>
					<div>
						<h4 class="report_title_sub">배당률</h4>
						<p>{{ props.data.top_box.배당률 }}</p>
					</div>
				</div>
			</div>
		</section>
		<section class="section_enterprise_overview">
			<div class="report_title_wrapper">
				<h2 class="report_title">기업 개요</h2>
				<AudioPlayer :src="props.audioFile" />
			</div>
			<div v-dompurify-html="props.data.company_outline" class="c_editorContent c_editorContent_outline"></div>
		</section>
		<section class="section_basic_info">
			<h2 class="report_title">기본 정보</h2>
			<div class="basic_info_content">
				<p v-if="props.date" class="base_date">{{ removeTimeFromDate(props.date) }} 기준</p>
				<div class="table_wrap">
					<div class="table_item">
						<p class="table_head">시가총액(백만)</p>
						<p class="table_data">{{ props.data.bottom_box.시가총액 }}</p>
					</div>
					<div class="table_item">
						<p class="table_head">발행주식수(천주)</p>
						<p class="table_data">{{ props.data.bottom_box.발행주식수 }}</p>
					</div>
					<div class="table_item">
						<p class="table_head">PBR(분기)</p>
						<p class="table_data">{{ props.data.bottom_box.PBR }}</p>
					</div>
					<div class="table_item">
						<p class="table_head">PER(분기)</p>
						<p class="table_data">{{ props.data.bottom_box.PER }}</p>
					</div>
					<div class="table_item">
						<p class="table_head">ROE(분기)</p>
						<p class="table_data">{{ props.data.bottom_box.ROE }}</p>
					</div>
					<div class="table_item">
						<p class="table_head">배당수익률(분기)</p>
						<p class="table_data_withPercent">
							{{ props.data.bottom_box.배당수익률 }}<span v-if="props.data.bottom_box.배당수익률 !== '-'">%</span>
						</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
// core
import { ref } from 'vue';

// component
// api
import { GetInvestReportOverviewRes } from '@/api/report';
// constant
import { COMPANY_ANALYSIS_TOOLTIP } from '@/constants/title';
// store
import { useDeviceStore } from '@/stores/device';
// store
import { useModalStore } from '@/stores/modal';
// util
import { priceFormat } from '@/utils/function/format';
import { removeTimeFromDate } from '@/utils/function/format';

import AudioPlayer from '../AudioPlayer.vue';

// -----variable-----
const props = defineProps<{
	data: GetInvestReportOverviewRes;
	date?: string;
	auth?: boolean;
	audioFile?: string;
}>();
const modalStore = useModalStore();
const deviceStore = useDeviceStore();
const isTooltipOpened = ref(false);
</script>

<style scoped lang="scss">
.wrap_overview {
	.report_title_wrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.8rem;
	}
	.report_title {
		font-size: var(--font-s-x-large);
		font-weight: var(--font-w-bold);
		line-height: 2.387rem;
		white-space: nowrap;
	}
	.report_title_sub {
		color: var(--color-base-light, #c6c6d6);
		font-size: var(--font-s-mid);
		font-weight: var(--font-w-light);
		line-height: 1.9rem;
	}
	.section_overview {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		width: 100%;
		margin: 0 auto;
		.overview_left,
		.overview_right {
			border-radius: 8px;
			background: var(--color-bg-mid, #212c3f);
		}
		.overview_left {
			position: relative;
			width: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 0.4rem;
			align-self: stretch;
			padding: 2rem;
			.title_score {
				display: flex;
				align-items: center;
				gap: 0.5rem;
				strong {
					color: var(--color-primary, var(--text-white-base, #ff5e00));
					text-align: center;
					font-size: var(--font-s-x-large);
					font-weight: var(--font-w-bold);
					line-height: normal;
				}
				span {
					font-size: var(--font-s-x-large);
					font-weight: var(--font-w-bold);
					line-height: normal;
				}
			}
			p {
				font-size: var(--font-s-mid);
				font-weight: var(--font-w-light);
				line-height: 1.9rem;
			}
		}
		.overview_left > div {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
		}
		.overview_left.locked > div {
			filter: blur(4px);
		}
		.overview_left.locked > img {
			width: 3rem;
			height: 3.8rem;
			position: absolute;
		}
		.overview_right {
			width: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			gap: 1.2rem;
			padding: 1.6rem;
			.tooltip_container {
				width: 100%;
				display: flex;
				justify-content: end;
				position: relative;
				.btn_tooltip {
					display: block;
					width: 2.4rem;
					height: 2.4rem;
					cursor: pointer;
				}
				.box_tooltip {
					position: absolute;
					top: 3rem;
					right: -1rem;
					z-index: 1;
					background-color: var(--color-bg-light);
					padding: 1.6rem;
					border-radius: 0.8rem;
					box-shadow: 4px 4px 10px 0px #0000004d;
					white-space: nowrap;
					.tooltip_title {
						font-size: var(--font-s-mid);
						font-weight: var(--font-w-light);
						line-height: 1.2;
					}
				}
			}
			.right_row {
				display: flex;
				align-items: flex-start;
				width: 100%;
				div {
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: center;
					width: 100%;
				}
				p {
					font-size: var(--font-s-mid);
					font-weight: var(--font-w-semi-bold);
					line-height: 1.79rem;
				}
			}
		}
	}
	.section_enterprise_overview {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}
	.section_basic_info {
		display: flex;
		flex-direction: column;
		gap: 2rem;
		.basic_info_content {
			display: flex;
			flex-direction: column;
			gap: 1rem;
			.base_date {
				color: var(--color-base-light, #c6c6d6);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: normal;
			}
			.table_wrap {
				display: grid;
				grid-template-columns: repeat(3, 1fr);
				grid-template-rows: repeat(2, 1fr);
				@media screen and (max-width: 1280px) {
					grid-template-columns: repeat(2, 1fr);
					grid-template-rows: repeat(3, 1fr);
				}
				.table_item {
					display: flex;
					flex-direction: column;
					.table_head {
						display: flex;
						padding: 0.8rem 1rem;
						justify-content: center;
						align-items: center;
						gap: 1rem;
						align-self: stretch;
						border: 1px solid var(--color-base-mid-dark, #393d45);
						background: var(--color-bg-mid, #212c3f);

						font-size: var(--font-s-mid);
						font-weight: var(--font-w-light);
						line-height: 1.9rem;
					}
					.table_data {
						display: flex;
						height: 3.5rem;
						padding: 0.8rem 1rem;
						justify-content: center;
						align-items: center;
						gap: 1rem;
						align-self: stretch;
						border: 1px solid var(--color-base-mid-dark, #393d45);
						font-size: var(--font-s-mid);
						font-weight: var(--font-w-semi-bold);
						line-height: normal;
					}
					.table_data_withPercent {
						display: flex;
						height: 3.5rem;
						padding: 0.8rem 1rem;
						justify-content: center;
						align-items: center;
						border: 1px solid var(--color-base-mid-dark, #393d45);
						font-size: var(--font-s-mid);
						font-weight: var(--font-w-semi-bold);
						line-height: normal;
					}
				}
			}
		}
	}
}
</style>
